import { useCallback, useEffect, useState } from 'react';
import { useRemoteSearchOptions, type SearchOption } from '../../../../../shared/hooks/use-remote-search-options';
import { apiErrorMessage } from '../../../../../shared/policies/api-error-message';
import { addOptionFor, ADD_OPTION_VALUE } from '../policies/tyre-catalogue-add.policy';

interface CatalogueEntry {
  id: string;
  name: string;
}

interface UseTyreCatalogueSelectOptions {
  /** The first page of active values, or the ones matching `q`. */
  fetch: (q: string | undefined) => Promise<CatalogueEntry[]>;
  /** Adds the value, or answers the one already there. */
  add: (name: string) => Promise<CatalogueEntry>;
  canAdd: boolean;
  value?: string;
  onChange?: (value: string | undefined) => void;
  /** The saved value's name, so it shows rather than its id when the form reopens on one the first page doesn't hold. */
  savedName?: string | null;
}

/** State behind a catalogue picker that can add what is typed: a failed add stays inline under the field, never a toast. */
export function useTyreCatalogueSelect({ fetch, add, canAdd, value, onChange, savedName }: UseTyreCatalogueSelectOptions) {
  const fetchOptions = useCallback(
    (q: string | undefined) => fetch(q).then((entries) => entries.map((entry) => ({ value: entry.id, label: entry.name }))),
    [fetch],
  );
  const { pin, selectProps } = useRemoteSearchOptions(fetchOptions);
  const [term, setTerm] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (value && savedName) pin({ value, label: savedName });
  }, [value, savedName, pin]);

  const addOption = addOptionFor(term, selectProps.options, canAdd);
  const options: SearchOption[] = addOption ? [...selectProps.options, addOption] : selectProps.options;

  async function addTyped() {
    setError(null);
    try {
      const entry = await add(term.trim());
      pin({ value: entry.id, label: entry.name });
      onChange?.(entry.id);
    } catch (err) {
      setError(apiErrorMessage(err) ?? 'Could not add that value. Try again.');
    }
  }

  return {
    error,
    selectProps: {
      ...selectProps,
      options,
      onSearch: (next: string) => {
        setTerm(next);
        selectProps.onSearch(next);
      },
      onOpenChange: (open: boolean) => {
        if (!open) setTerm('');
        selectProps.onOpenChange(open);
      },
      onChange: (next: string | undefined) => {
        if (next === ADD_OPTION_VALUE) {
          void addTyped();
          return;
        }
        setError(null);
        onChange?.(next);
      },
    },
  };
}
